import { Notification } from '../hooks/useNotifications';
import axiosInstance from '../config/axios';

interface NotificationResponse {
  success: boolean;
  data: Notification[];
  total: number;
  page: number;
  limit: number;
}

interface CreateNotificationRequest {
  title: string;
  message: string;
  type: Notification['type'];
  category?: Notification['category'];
  priority?: Notification['priority'];
  actionUrl?: string;
  actionText?: string;
  targetUserId?: string;
  stageId?: string;
  appName?: string;
}

interface NotificationFilters {
  read?: boolean;
  category?: Notification['category'];
  priority?: Notification['priority'];
  type?: Notification['type'];
  partyId?: string;
  userId?: string;
  stageId?: string;
  appName?: string;
  page?: number;
  limit?: number;
}

class NotificationService {
  private baseURL = '/notifications';

  /**
   * Get notifications for the current user
   */
  async getNotifications(filters: NotificationFilters = {}): Promise<NotificationResponse> {
    try {
      const response = await axiosInstance.get(this.baseURL, { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: string): Promise<{ success: boolean }> {
    try {
      const response = await axiosInstance.patch(`${this.baseURL}/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark a notification as unread
   */
  async markAsUnread(notificationId: string): Promise<{ success: boolean }> {
    try {
      const response = await axiosInstance.patch(`${this.baseURL}/${notificationId}/unread`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification as unread:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read for the current user
   */
  async markAllAsRead(filters: { stageId?: string; appName?: string; partyId?: string; userId?: string } = {}): Promise<{ success: boolean; count: number }> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/mark-all-read`, {}, { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId: string): Promise<{ success: boolean }> {
    try {
      const response = await axiosInstance.delete(`${this.baseURL}/${notificationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  /**
   * Clear all notifications for the current user
   */
  async clearAllNotifications(filters: { stageId?: string; appName?: string; partyId?: string; userId?: string } = {}): Promise<{ success: boolean; count: number }> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/clear-all`, {}, { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      throw error;
    }
  }

  /**
   * Create a new notification (admin only)
   */
  async createNotification(notification: CreateNotificationRequest): Promise<Notification> {
    try {
      const response = await axiosInstance.post(this.baseURL, notification);
      return response.data;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  /**
   * Get notification statistics
   */
  async getStats(filters: { stageId?: string; appName?: string } = {}): Promise<{
    total: number;
    unread: number;
    byCategory: Record<string, number>;
    byPriority: Record<string, number>;
  }> {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/stats`, { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching notification stats:', error);
      throw error;
    }
  }

  /**
   * Update notification preferences
   */
  async updatePreferences(preferences: {
    emailNotifications?: boolean;
    pushNotifications?: boolean;
    categories?: Notification['category'][];
  }): Promise<{ success: boolean }> {
    try {
      const response = await axiosInstance.put(`${this.baseURL}/preferences`, preferences);
      return response.data;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }

  /**
   * Get notification preferences
   */
  async getPreferences(): Promise<{
    emailNotifications: boolean;
    pushNotifications: boolean;
    categories: Notification['category'][];
  }> {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/preferences`);
      return response.data;
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      throw error;
    }
  }

  /**
   * Get expense code details for notification approval modal
   */
  async getExpenseCodeForNotification(expenseCodeId: string): Promise<{ success: boolean; data: any }> {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/expense-code/${expenseCodeId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching expense code for notification:', error);
      throw error;
    }
  }

  /**
   * Approve expense code from notification
   */
  async approveExpenseCodeFromNotification(expenseCodeId: string): Promise<{ success: boolean; accountCode?: string; message?: string }> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/expense-code/${expenseCodeId}/approve`);
      return response.data;
    } catch (error) {
      console.error('Error approving expense code from notification:', error);
      throw error;
    }
  }

  /**
   * Reject expense code from notification
   */
  async rejectExpenseCodeFromNotification(expenseCodeId: string, reason: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/expense-code/${expenseCodeId}/reject`, { reason });
      return response.data;
    } catch (error) {
      console.error('Error rejecting expense code from notification:', error);
      throw error;
    }
  }

  /**
   * Get income code details for notification approval
   */
  async getIncomeCodeForNotification(incomeCodeId: string): Promise<{ success: boolean; data: any }> {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/income-code/${incomeCodeId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting income code for notification:', error);
      throw error;
    }
  }

  /**
   * Approve income code from notification
   */
  async approveIncomeCodeFromNotification(incomeCodeId: string): Promise<{ success: boolean; accountCode?: string; message?: string }> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/income-code/${incomeCodeId}/approve`);
      return response.data;
    } catch (error) {
      console.error('Error approving income code from notification:', error);
      throw error;
    }
  }

  /**
   * Reject income code from notification
   */
  async rejectIncomeCodeFromNotification(incomeCodeId: string, rejectionReason: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/income-code/${incomeCodeId}/reject`, { rejectionReason });
      return response.data;
    } catch (error) {
      console.error('Error rejecting income code from notification:', error);
      throw error;
    }
  }

  /**
   * Get project code details for notification approval
   */
  async getProjectCodeForNotification(projectCodeId: string): Promise<{ success: boolean; data: any }> {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/project-code/${projectCodeId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting project code for notification:', error);
      throw error;
    }
  }

  /**
   * Approve project code from notification
   */
  async approveProjectCodeFromNotification(projectCodeId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/project-code/${projectCodeId}/approve`);
      return response.data;
    } catch (error) {
      console.error('Error approving project code from notification:', error);
      throw error;
    }
  }

  /**
   * Reject project code from notification
   */
  async rejectProjectCodeFromNotification(projectCodeId: string, rejectionReason: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/project-code/${projectCodeId}/reject`, {
        rejectionReason
      });
      return response.data;
    } catch (error) {
      console.error('Error rejecting project code from notification:', error);
      throw error;
    }
  }

  /**
   * Get specific project code details for notification approval
   */
  async getSpecificProjectCodeForNotification(specificProjectCodeId: string): Promise<{ success: boolean; data: any }> {
    try {
      const response = await axiosInstance.get(`${this.baseURL}/specific-project-code/${specificProjectCodeId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting specific project code for notification:', error);
      throw error;
    }
  }

  /**
   * Approve specific project code from notification
   */
  async approveSpecificProjectCodeFromNotification(specificProjectCodeId: string): Promise<{ success: boolean; specificCode?: string; message?: string }> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/specific-project-code/${specificProjectCodeId}/approve`);
      return response.data;
    } catch (error) {
      console.error('Error approving specific project code from notification:', error);
      throw error;
    }
  }

  /**
   * Reject specific project code from notification
   */
  async rejectSpecificProjectCodeFromNotification(specificProjectCodeId: string, rejectionReason: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await axiosInstance.post(`${this.baseURL}/specific-project-code/${specificProjectCodeId}/reject`, {
        rejectionReason
      });
      return response.data;
    } catch (error) {
      console.error('Error rejecting specific project code from notification:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time notifications via WebSocket
   */
  subscribeToRealTime(onNotification: (notification: Notification) => void): () => void {
    const token = localStorage.getItem('accessToken');
    const baseURL = window.location.origin.replace('http', 'ws');
    const wsUrl = `${baseURL}/ws/notifications?token=${token}`;
    
    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      try {
        const notification = JSON.parse(event.data);
        onNotification(notification);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return () => {
      ws.close();
    };
  }
}

export const notificationService = new NotificationService();
export default notificationService;